import { meResponseSchema, type MeResponse } from '@coworker/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../lib/api-error.js';

/**
 * `GET /v1/me` - AUTH-009, WEB-005.
 *
 * The first call after a login and the only one that answers "does this token
 * belong to somebody we know". It carries the caller's workspaces in the same
 * response, which is why the shell can render without a second round trip.
 *
 * A `403 USER_NOT_PROVISIONED` is not a failure: it is a real token whose
 * subject has no `app_user` row, which is precisely what AUTH-010 exists to
 * create. Treating it as an error would show a signed-in person an error page on
 * the one screen that can fix their state.
 */
export const SESSION_QUERY_KEY = ['session'] as const;

export interface SessionState {
  readonly query: UseQueryResult<MeResponse, unknown>;
  /** A valid token with no profile behind it. The onboarding screen owns this. */
  readonly needsOnboarding: boolean;
  /**
   * The token was refused. Both shapes count: the ADR-030 envelope, and the
   * gateway's own 401, which carries no code at all because an HTTP API cannot
   * customise its authorizer's body.
   */
  readonly isUnauthenticated: boolean;
}

export function useSession(): SessionState {
  const { api, status } = useAuth();

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    enabled: status === 'signedIn',
    queryFn: () => api.request('/v1/me', { schema: meResponseSchema }),
  });

  return {
    query,
    needsOnboarding: query.error instanceof ApiError && query.error.needsOnboarding,
    isUnauthenticated: query.error instanceof ApiError && query.error.isUnauthenticated,
  };
}
